import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('admin_token'));

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const storedToken = localStorage.getItem('admin_token');
    if (storedToken) {
      try {
        const response = await axios.get(`${API}/auth/check`, {
          headers: { Authorization: `Bearer ${storedToken}` }
        });
        setUser(response.data);
        setToken(storedToken);
      } catch (error) {
        localStorage.removeItem('admin_token');
        setToken(null);
        setUser(null);
      }
    }
    setLoading(false);
  };

  const login = async (username, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, {
        username,
        password
      });
      const { access_token, username: userName } = response.data;
      localStorage.setItem('admin_token', access_token);
      setToken(access_token);
      setUser({ username: userName });
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Login failed'
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('admin_token');
    setToken(null);
    setUser(null);
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      await axios.post(
        `${API}/auth/change-password`,
        {
          current_password: currentPassword,
          new_password: newPassword
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to change password'
      };
    }
  };

  const changeUsername = async (newUsername, password) => {
    try {
      const response = await axios.post(
        `${API}/auth/change-username`,
        {
          new_username: newUsername,
          password: password
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      // Update local user state with new username
      setUser({ username: response.data.new_username });
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to change username'
      };
    }
  };

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    checkAuth,
    changePassword,
    changeUsername
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
